import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { COLORS, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "../theme.js";
import { Field, ErrorNote, Spinner } from "../components/ui.jsx";
import { useAuth } from "../auth/AuthProvider.jsx";
import { uploadPhoto, deletePhoto, validateImage } from "../lib/storage.js";
import { primaryBtnStyle, secondaryBtnStyle, ghostBtnStyle, btnState } from "../components/styles.js";
import { IdentityFields, PurchaseFields, DnaFields } from "../components/ItemFields.jsx";
import CatalogueSearch from "../components/CatalogueSearch.jsx";
import { CATEGORIES, CATEGORY_KEYS, categoryMeta } from "../domain/categories.js";
import { generatePreservationProfile } from "../domain/preservation.js";
import { lookupBarcode, materialsForForm } from "../domain/products.js";
import { blankForm, payloadFromForm, isFormComplete, withCategory } from "../domain/itemForm.js";

/* ZXing decodes barcodes in JavaScript, which is what makes scanning work on
   iOS — but it is around 450 KB, and the scanner sits behind a button most
   people never press. Loading it on demand keeps that weight out of the first
   paint, the same trade already made for the charting screens. */
const BarcodeScanner = lazy(() => import("../components/BarcodeScanner.jsx"));

/* 5 steps: category → identity → purchase → DNA → preservation profile. */
export default function AddItemWizard({ onAdd, onCancel }) {
  const [step, setStep] = useState(0);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanNote, setScanNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [form, setForm] = useState(() => blankForm("Sneakers"));
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const meta = categoryMeta(form.category);

  /* Photos upload as soon as they're picked, so the item row can be created
     with its paths already in place. Previews come from object URLs rather
     than a signing round trip — the file is already on this device. */
  const { userId } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState([]); // parallel to form.photos
  const previewsRef = useRef(previews);
  previewsRef.current = previews;
  useEffect(() => () => previewsRef.current.forEach((url) => URL.revokeObjectURL(url)), []);

  async function handlePhotoFiles(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !userId) return;

    for (const file of files) {
      const problem = validateImage(file);
      if (problem) { setSaveError(problem); return; }
    }

    setUploading(true);
    setSaveError(null);
    try {
      for (const file of files) {
        const path = await uploadPhoto(userId, "items", file);
        setForm((prev) => ({ ...prev, photos: [...prev.photos, path] }));
        setPreviews((prev) => [...prev, URL.createObjectURL(file)]);
      }
    } catch {
      setSaveError("Couldn't upload that photo. Check your connection and try again.");
    } finally {
      setUploading(false);
    }
  }

  function removePhoto(index) {
    URL.revokeObjectURL(previews[index]);
    // The file is already in the bucket but no row will ever reference it.
    deletePhoto(form.photos[index]).catch(() => {});
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    setForm((prev) => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));
  }

  /* Abandoning the wizard strands every photo it uploaded: the item row that
     was going to reference them is never created, so nothing else will ever
     clean them up. Deleting them here is best effort — failing to tidy up is
     not a reason to keep the user on a form they've asked to leave. */
  function handleCancel() {
    for (const path of form.photos) deletePhoto(path).catch(() => {});
    onCancel();
  }

  /* A scan closes the camera immediately — holding it open over a network
     round trip looks like a failed scan — and the catalogue lookup reports
     itself in the note underneath the scan button.

     A miss and a failed lookup are kept distinct. "Not in the catalogue" tells
     someone to type the details in; "the lookup failed" tells them to try
     again. Collapsing the two would have people hand-entering a shoe that is
     catalogued, because their signal dropped for a second. */
  async function handleScannedCode(code) {
    setScannerOpen(false);
    setScanNote("Looking that code up…");

    const { product, error, unsupported } = await lookupBarcode(code);

    if (unsupported) {
      // Say what is actually true. "No match found" would imply the catalogue
      // was consulted and came back empty, and would have people rescanning a
      // shoe that could never have matched.
      setScanNote(`Scanned ${code}. The catalogue doesn't carry barcodes yet — search it by brand below, or type the details in.`);
      return;
    }
    if (error) {
      setScanNote("Couldn't reach the catalogue — check your connection, or enter the details manually.");
      return;
    }
    if (!product) {
      setScanNote("No match found for that code — search by brand below, or enter the details manually.");
      return;
    }

    applyProduct(product);
    setScanNote(`Matched: ${product.brand} ${product.model}`);
  }

  /* Fills the form from a catalogue row, however it was found — scanned or
     searched. Only the fields the catalogue actually knows: what a collector
     paid, and what the piece is worth to them, are theirs to enter. */
  function applyProduct(product) {
    setForm((prev) => {
      // A catalogue row knows its own category, and the DNA groups depend on
      // it, so switch first and map the materials against the new category.
      const next = product.category && product.category !== prev.category
        ? withCategory(prev, product.category)
        : prev;
      return {
        ...next,
        brand: product.brand,
        model: product.model,
        colorway: product.colorway,
        retail: product.retail == null ? next.retail : String(product.retail),
        materials: { ...next.materials, ...materialsForForm(next.category, product.materials) },
      };
    });
    // The category picker is step 0; picking a catalogue row has answered it.
    setStep((s) => (s === 0 ? 1 : s));
  }

  function pickCategory(key) {
    setForm((prev) => withCategory(prev, key));
    setStep(1);
  }

  const profile = useMemo(
    () => generatePreservationProfile({ category: form.category, status: form.status, materials: form.materials }),
    [form.category, form.status, form.materials]
  );

  const canNext1 = isFormComplete(form);

  async function handleSave() {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    const { error } = await onAdd(payloadFromForm(form));
    if (error) {
      setSaveError(error);
      setSaving(false);
    }
    // On success the parent unmounts this wizard.
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {scannerOpen && (
        <Suspense fallback={null}>
          <BarcodeScanner
            onClose={() => setScannerOpen(false)}
            onDetected={handleScannedCode}
          />
        </Suspense>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={handleCancel} style={ghostBtnStyle}>
          ← Add New Item
        </button>
      </div>

      {step > 0 && (
        <>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {[1, 2, 3, 4].map((n) => (
              <div
                key={n}
                style={{
                  width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: FONT_MONO, fontSize: 11.5,
                  background: n === step ? `linear-gradient(145deg, ${COLORS.goldBright}, ${COLORS.gold})` : "transparent",
                  color: n === step ? "#1A1408" : n < step ? COLORS.goldBright : COLORS.creamFaint,
                  border: `1px solid ${n <= step ? COLORS.gold : COLORS.line}`,
                }}
              >
                {n}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 11, fontFamily: FONT_MONO, color: COLORS.creamFaint, letterSpacing: 0.5, textTransform: "uppercase" }}>Step {step} of 4</span>
            <button onClick={() => setStep(0)} style={{ ...ghostBtnStyle, fontSize: 11.5, color: COLORS.goldBright }}>
              {meta.icon} {meta.label} · change
            </button>
          </div>
        </>
      )}

      {step === 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 19, color: COLORS.cream }}>What are you adding?</div>
          <div style={{ fontSize: 12.5, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: -6 }}>
            The rest of the form adapts to the type you pick — materials, sizing, and preservation guidance all change.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 4 }}>
            {CATEGORY_KEYS.map((k) => {
              const c = CATEGORIES[k];
              const active = form.category === k;
              return (
                <button
                  key={k}
                  onClick={() => pickCategory(k)}
                  style={{
                    background: active ? "rgba(201,162,39,0.12)" : COLORS.blackCard,
                    border: `1px solid ${active ? COLORS.gold : COLORS.lineFaint}`,
                    borderRadius: 14, padding: "20px 14px", cursor: "pointer",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                  }}
                >
                  <span style={{ fontSize: 30 }}>{c.icon}</span>
                  <span style={{ fontFamily: FONT_DISPLAY, fontSize: 15, color: active ? COLORS.goldBright : COLORS.cream }}>{c.label}</span>
                </button>
              );
            })}
          </div>
          <button onClick={handleCancel} style={{ ...secondaryBtnStyle, marginTop: 6 }}>
            Cancel
          </button>
        </div>
      )}

      {step === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 19, color: COLORS.cream }}>Identity</div>

          <Field label="Photos">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {previews.map((src, i) => (
                <div key={src} style={{ position: "relative", width: 72, height: 72 }}>
                  <img src={src} alt="" style={{ width: 72, height: 72, borderRadius: 10, objectFit: "cover", border: `1px solid ${COLORS.lineFaint}` }} />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    aria-label="Remove photo"
                    style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", background: COLORS.red, color: "#fff", border: "none", fontSize: 11, cursor: "pointer", lineHeight: "20px" }}
                  >
                    ×
                  </button>
                </div>
              ))}
              <label
                style={{
                  width: 72, height: 72, borderRadius: 10, border: `1px dashed ${COLORS.line}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: uploading ? "wait" : "pointer", color: COLORS.creamFaint, fontSize: 22, fontFamily: FONT_BODY,
                }}
              >
                {uploading ? <Spinner size={16} /> : "+"}
                <input type="file" accept="image/*" multiple disabled={uploading} onChange={handlePhotoFiles} style={{ display: "none" }} />
              </label>
            </div>
          </Field>

          <IdentityFields
            form={form}
            set={set}
            scanNote={scanNote}
            onScan={() => { setScanNote(""); setScannerOpen(true); }}
            catalogueSearch={
              <CatalogueSearch
                category={form.category}
                onPick={(product) => {
                  applyProduct(product);
                  setScanNote(`Filled from the catalogue: ${product.brand} ${product.model}`);
                }}
              />
            }
          />
          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <button onClick={() => setStep(0)} style={secondaryBtnStyle}>Back</button>
            <button disabled={!canNext1} onClick={() => setStep(2)} style={btnState(primaryBtnStyle, canNext1)}>Next</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 19, color: COLORS.cream }}>Purchase Information</div>
          <PurchaseFields form={form} set={set} />
          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <button onClick={() => setStep(1)} style={secondaryBtnStyle}>Back</button>
            <button onClick={() => setStep(3)} style={primaryBtnStyle}>Next</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 19, color: COLORS.cream }}>{meta.dnaTitle}</div>
          <DnaFields form={form} setForm={setForm} />
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep(2)} style={secondaryBtnStyle}>Back</button>
            <button onClick={() => setStep(4)} style={primaryBtnStyle}>Next</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 19, color: COLORS.cream }}>Preservation Profile</div>
          <div style={{ background: COLORS.blackCard, border: `1px solid ${COLORS.line}`, borderRadius: 14, padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 0.6, color: COLORS.goldBright, textTransform: "uppercase" }}>
                {meta.label} Preservation Profile
              </span>
            </div>
            <div style={{ fontSize: 11, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginBottom: 12 }}>
              Generated from the materials you selected — a rule-based estimate, not a live lookup.
            </div>
            {profile.map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 14, padding: "9px 0", borderTop: `1px solid ${COLORS.lineFaint}`, fontSize: 13, fontFamily: FONT_BODY }}>
                <span style={{ color: COLORS.creamDim, flex: "none" }}>{k}</span>
                <span style={{ color: COLORS.cream, fontWeight: 500, textAlign: "right" }}>{v}</span>
              </div>
            ))}
          </div>

          <ErrorNote message={saveError} />

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep(3)} disabled={saving} style={btnState(secondaryBtnStyle, !saving)}>Back</button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ ...btnState(primaryBtnStyle, !saving), display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            >
              {saving && <Spinner size={14} color="#1A1408" />}
              {saving ? "Saving…" : "Save to Passport"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
